import { Entity, bridge, time } from "@dinahmoe/dmafjs-core";

/**
 * Generic navigation component for DMAF applications
 *
 * Handles:
 * - Internal page routing (with layouts)
 * - External links (with security)
 * - Popup windows
 * - Clipboard copy
 * - Social media sharing
 *
 * Replaces dmafjs-links functionality with unified routes-based system.
 */
class Navigation extends Entity {
  namespace = "navigation:controller";
  engine = "navigation";

  init(node) {
    // Validate routes config
    if (!node.routes || node.routes.length === 0) {
      console.warn("Navigation: routes config is empty or missing");
      node.routes = [];
    }

    // Build route ID→route map for quick lookup
    node._routesById = new Map();
    // Separate static and dynamic routes for efficient matching
    node._staticRoutes = new Map();
    node._dynamicRoutes = [];

    for (const route of node.routes) {
      if (route.id) {
        node._routesById.set(route.id, route);
      }

      // Parse route path for dynamic segments
      if (route.path && !this.isExternalRoute(route)) {
        const parsed = this._parseRoutePath(route.path);
        route._parsed = parsed;

        if (parsed.isDynamic) {
          node._dynamicRoutes.push(route);
        } else {
          node._staticRoutes.set(route.path, route);
        }
      }
    }

    bridge.create({...this.getInitData(node), uid: this.engine + "/" + node.uid, sender: "core"});
  }

  /**
   * Parse a route path to identify parameter segments
   * "/session/:entityUid" → { isDynamic: true, paramNames: ["entityUid"], regex: /^\/session\/([^\/]+)$/ }
   */
  _parseRoutePath(path) {
    const paramNames = [];
    const segments = path.split('/');

    // Check if path has any dynamic segments
    const hasDynamicSegments = segments.some(s => s.startsWith(':'));

    if (!hasDynamicSegments) {
      return { isDynamic: false, paramNames: [], regex: null };
    }

    // Build regex pattern
    const regexParts = segments.map(segment => {
      if (segment.startsWith(':')) {
        const paramName = segment.slice(1);
        paramNames.push(paramName);
        return '([^\\/]+)';
      }
      // Escape special regex characters in static segments
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    });

    const regex = new RegExp('^' + regexParts.join('\\/') + '\\/?$');

    return { isDynamic: true, paramNames, regex };
  }

  receiveMessage(node, message) {
    node.title = message.value.title;
    node.hash = message.value.hash;
    node.queryParams = message.value.queryParams;
    node.href = message.value.href;
    node.pathname = message.value.pathname;

    // Match pathname to route and extract params
    const match = this._matchRoute(node, node.pathname);
    node._currentParams = match?.params || {};

    if (match?.route?.id) {
      node.currentRouteId = match.route.id;
    }

    // Only auto-dispatch if enabled (default true) or after first manual dispatch
    // This allows apps to control when navigation starts (e.g., after auth check)
    if (node.autoDispatch !== false || node._hasDispatchedOnce) {
      this.dispatchNavigationState(node);
    }
  }

  /**
   * Explicitly dispatch current navigation state.
   * Call this after auth check to trigger initial navigation for protected routes.
   * This method can be called from action configs.
   */
  dispatchCurrentState(node, config, event) {
    node._hasDispatchedOnce = true;
    this.dispatchNavigationState(node);
  }

  /**
   * Match a pathname to a route, extracting any dynamic parameters
   * Returns { route, params } or null if no match
   */
  _matchRoute(node, pathname) {
    // Normalize trailing slash for matching
    const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

    // Fast path: try exact match first (static routes)
    const staticRoute = node._staticRoutes.get(normalized) || node._staticRoutes.get(pathname);
    if (staticRoute) {
      return { route: staticRoute, params: {} };
    }

    // Try dynamic routes
    for (const route of node._dynamicRoutes) {
      const parsed = route._parsed;
      if (!parsed?.regex) continue;

      const match = pathname.match(parsed.regex);
      if (match) {
        // Extract params from matched groups
        const params = {};
        parsed.paramNames.forEach((name, index) => {
          params[name] = decodeURIComponent(match[index + 1]);
        });
        return { route, params };
      }
    }

    return null;
  }

  dispatchNavigationState(node) {
    // Match pathname to route
    const match = this._matchRoute(node, node.pathname);
    const route = match?.route;
    const params = match?.params || node._currentParams || {};

    // Expose params on node for template bindings (e.g., app_navigation.params.entityUid)
    node.params = params;

    // Get templateUid from matched route, or fallback to home route
    const templateUid = route?.layout || node._staticRoutes.get("/")?.layout || null;

    const eventData = {
      title: node.title,
      pathname: node.pathname,
      hash: node.hash,
      queryParams: node.queryParams,
      href: node.href,
      templateUid: templateUid,
      source: node._navigationSource,
      routeId: node.currentRouteId,
      params: params,
      public: route?.public || false,
    };

    // Main state event (always emitted on any URL change)
    this.dispatch(`${node.uid}:current_state`, time.current, eventData);

    // Clear source after dispatch
    node._navigationSource = null;
  }

  navigate(node, config, event) {
    // Explicit navigation should always dispatch, even if autoDispatch is false
    node._hasDispatchedOnce = true;

    // Store source from incoming event for pass-through to current_state dispatch
    node._navigationSource = event.data?.source || null;

    // Get route ID from config or extract from event name
    // Config: { routeId: "session", params: { entityUid: "abc" } }
    // Event name: "www_navigation:navigate:about" → "about"
    const routeId = config.routeId || event.name.split(':').pop();

    // Get params from config or event data
    const params = config.params || event.data?.params || {};

    // Find the route by ID
    const route = node._routesById.get(routeId);

    if (!route) {
      console.warn(`Unknown route ID: ${routeId}`);
      return;
    }

    // Update current route ID and params
    node.currentRouteId = routeId;
    node._currentParams = params;

    // Get newTab option from config
    const newTab = config.newTab || false;

    // Route by action type
    const action = route.action || 'navigate';

    switch (action) {
      case 'clipboard':
        this.handleClipboard(node, route);
        break;
      case 'share':
        this.handleShare(node, route);
        break;
      case 'navigate':
      default:
        this.handleNavigate(node, route, params, newTab);
        break;
    }
  }

  handleNavigate(node, route, params = {}, newTab = false) {
    // Check if this is an external link
    if (this.isExternalRoute(route)) {
      // External link: open in new tab/window or follow mailto/tel links
      if (route.path.startsWith('mailto:') || route.path.startsWith('tel:')) {
        // Mailto and tel links open directly in same window
        window.location.href = route.path;
      } else if (route.popup) {
        // Open as popup window with specified dimensions
        this.openPopup(route.path, route.popup);
      } else {
        // HTTP/HTTPS links: open in new tab with security attributes
        const target = route.target || '_blank';
        const features = route.features || 'noopener,noreferrer';
        window.open(route.path, target, features);
      }
    } else {
      // Internal navigation: build URL and update state
      const pathname = this._buildPath(route.path, params);

      if (newTab) {
        // Open in new browser tab via environment
        const url = new URL(pathname, window.location.origin);
        bridge.update({
          uid: this.engine + "/" + node.uid,
          key: "openInNewTab",
          value: { url: url.href },
          sender: "core",
        });
      } else {
        // Normal navigation: update browser state
        this.setNavigationState(node, { pathname });
      }
    }
  }

  /**
   * Build a URL path from a route pattern and params
   * "/session/:entityUid" + { entityUid: "abc" } → "/session/abc"
   */
  _buildPath(routePath, params = {}) {
    if (!routePath) return '/';

    // Replace :paramName with actual values
    let path = routePath;
    for (const [key, value] of Object.entries(params)) {
      const placeholder = `:${key}`;
      if (path.includes(placeholder)) {
        path = path.replace(placeholder, encodeURIComponent(value));
      }
    }

    // Check for any remaining unsubstituted params
    const remainingParams = path.match(/:([^/]+)/g);
    if (remainingParams) {
      console.warn(`Missing required params for route: ${remainingParams.join(', ')}`);
    }

    return path;
  }

  openPopup(url, config) {
    const width = config.width || 800;
    const height = config.height || 600;
    const name = config.name || '_blank';
    const features = `toolbar=0,status=0,width=${width},height=${height},noopener,noreferrer`;
    window.open(url, name, features);
  }

  handleClipboard(node, route) {
    const text = route.clipboard?.text || route.clipboard?.url || '';

    if (!text) {
      console.warn('Clipboard action requires text or url property');
      return;
    }

    navigator.clipboard.writeText(text)
      .then(() => {
        this.dispatch(`${node.uid}:clipboard:success`, time.current, {
          text: text,
          message: route.clipboard?.successMessage || 'Copied to clipboard'
        });
      })
      .catch(err => {
        console.error('Clipboard copy failed:', err);
        this.dispatch(`${node.uid}:clipboard:error`, time.current, {
          error: err.message
        });
      });
  }

  handleShare(node, route) {
    const platform = route.share?.platform;
    const url = route.share?.url || window.location.href;

    if (!platform) {
      console.warn('Share action requires platform property');
      return;
    }

    switch (platform) {
      case 'facebook':
        this.shareFacebook(url);
        break;
      case 'twitter':
        this.shareTwitter(url, route.share);
        break;
      case 'linkedin':
        this.shareLinkedin(url);
        break;
      case 'pinterest':
        this.sharePinterest(url);
        break;
      default:
        console.warn(`Unknown share platform: ${platform}`);
    }
  }

  shareFacebook(url) {
    const shareUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(url)}`;
    this.openPopup(shareUrl, { width: 626, height: 797, name: 'facebook_share' });
  }

  shareTwitter(url, config) {
    const text = config?.text || '';
    const hashtags = config?.hashtags ? config.hashtags.join(',') : '';
    let shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`;

    if (text) {
      shareUrl += `&text=${encodeURIComponent(text)}`;
    }
    if (hashtags) {
      shareUrl += `&hashtags=${encodeURIComponent(hashtags)}`;
    }

    this.openPopup(shareUrl, { width: 626, height: 436, name: 'twitter_share' });
  }

  shareLinkedin(url) {
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    this.openPopup(shareUrl, { width: 803, height: 723, name: 'linkedin_share' });
  }

  sharePinterest(url) {
    const shareUrl = `https://pinterest.com/pin/create/button?url=${encodeURIComponent(url)}`;
    this.openPopup(shareUrl, { width: 626, height: 797, name: 'pinterest_share' });
  }

  isExternalRoute(route) {
    // Auto-detect or use explicit flag
    return route.external ||
           route.path?.startsWith('http://') ||
           route.path?.startsWith('https://') ||
           route.path?.startsWith('mailto:') ||
           route.path?.startsWith('tel:');
  }

  setNavigationState(node, config) {
    let updateUrl = false;

    if (config.href && node.href != config.href){
      node.href = config.href;
      updateUrl = true;
    }

    if (config.pathname && node.pathname != config.pathname){
      node.pathname = config.pathname;
      updateUrl = true;
    }

    if (config.hash && node.hash != config.hash){
      node.hash = config.hash;
      updateUrl = true;
    }

    if (config.title && node.title != config.title){
      node.title = config.title;
      updateUrl = true;
    }

    if (config.queryParams && node.queryParams != config.queryParams){
      node.queryParams = config.queryParams;
      updateUrl = true;
    }

    if(!updateUrl){
      this.dispatchNavigationState(node);
      return;
    }

    bridge.update({
      uid: this.engine + "/" + node.uid,
      key: "setNavigationState",
      value: config,
      sender: "core",
    });
  }

  /**
   * Get route by path (supports dynamic routes)
   * Returns { route, params } or null
   */
  getRoute(node, config) {
    const path = config.path || config;
    const match = this._matchRoute(node, path);
    return match || null;
  }

  /**
   * Get route by ID
   */
  getRouteById(node, id) {
    return node._routesById.get(id);
  }
}

const navigation = new Navigation();
export { navigation, Navigation };
